import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock } from 'lucide-react';
import SiteLayout, { PALETTE } from './SiteLayout';
import SEO from '../../components/SEO';
import { NewsletterSignup } from '../../components/NewsletterSignup';
import { GUIDES, GUIDE_CATEGORIES } from '../../seo/guidesConfig';
import { useLocation } from 'react-router-dom';
import { splitLangPath, getDict, tr, langHref, MARKETING_LANGS } from '../../i18n/marketing';
import { ORG_JSONLD, buildBreadcrumb, SITE_URL } from '../../seo/landingConfig';

const serif = "'Playfair Display', Georgia, serif";

export default function GuidesIndex() {
  const { pathname } = useLocation();
  const { lang } = splitLangPath(pathname);
  const dict = lang !== 'en' ? getDict(lang) : null;
  const t = (s) => tr(dict, s);
  const guides = dict ? GUIDES.map((g) => ({ ...g, title: tr(dict, g.title), cardSummary: tr(dict, g.cardSummary) })) : GUIDES;
  const alternates = [
    { hreflang: 'en', href: `${SITE_URL}/guides` },
    ...MARKETING_LANGS.map((l) => ({ hreflang: l, href: `${SITE_URL}/${l}/guides` })),
    { hreflang: 'x-default', href: `${SITE_URL}/guides` },
  ];
  const jsonLd = [
    ORG_JSONLD,
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Guides for food producers, traceability, audits, recalls',
      url: `${SITE_URL}/guides`,
      description: 'Practical, long-form guides for food producers: mock recalls, BRCGS traceability, mass balance, FSAI inspections, batch coding and HACCP records.',
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: GUIDES.map((g, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          url: `${SITE_URL}/guides/${g.slug}`,
          name: g.title,
        })),
      },
    },
    buildBreadcrumb([
      { name: 'Home', url: '/' },
      { name: 'Guides', url: '/guides' },
    ]),
  ];

  return (
    <SiteLayout>
      <SEO
        title="Guides, Traceability, Audits & Recalls for Food Producers"
        description="Practical guides for food producers: mock recalls, BRCGS traceability, the 4-hour mass balance, FSAI inspection prep, batch coding, HACCP records."
        path={langHref(lang, '/guides')}
        lang={lang}
        alternates={alternates}
        keywords="food traceability guides, mock recall guide, BRCGS traceability guide, FSAI inspection guide, HACCP records guide, batch coding guide, food producer resources"
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section data-testid="guides-index-hero" style={{ background: PALETTE.cream, padding: '128px 32px 72px', borderBottom: `1px solid ${PALETTE.border}` }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          <div style={{ fontSize: 11, letterSpacing: '0.24em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 18 }}>
            Guides · Free, no email gate
          </div>
          <h1 style={{ fontFamily: serif, fontSize: 'clamp(34px, 4.6vw, 58px)', fontWeight: 700, color: PALETTE.ink, margin: 0, letterSpacing: '-0.02em', lineHeight: 1.05 }}>
            {t('The working library for')}<br />
            <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>{t('audit-ready food production.')}</em>
          </h1>
          <p style={{ fontSize: 17, lineHeight: 1.7, color: PALETTE.slate, marginTop: 22, maxWidth: 720 }}>
            {t('Long-form, practical guides written from the production floor, mock recalls, BRCGS traceability, mass balance, FSAI inspections, batch coding, HACCP records. No fluff, no gate, cross-referenced to the standards they cover.')}
          </p>
        </div>
      </section>

      {/* GUIDE CARDS by category */}
      <section style={{ background: '#fff', padding: '72px 32px' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          {GUIDE_CATEGORIES.map((cat) => (
            <div key={cat} style={{ marginBottom: 56 }}>
              <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 20, paddingBottom: 10, borderBottom: `1px solid ${PALETTE.border}` }}>
                {cat}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
                {guides.filter((g) => g.category === cat).map((g) => (
                  <Link
                    key={g.slug}
                    to={langHref(lang, `/guides/${g.slug}`)}
                    data-testid={`guide-card-${g.slug}`}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      background: PALETTE.paper,
                      border: `1px solid ${PALETTE.border}`,
                      borderTop: `3px solid ${PALETTE.gold}`,
                      padding: '24px 26px',
                      textDecoration: 'none',
                      transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 10px 28px rgba(10,37,64,0.10)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <h2 style={{ fontFamily: serif, fontSize: 20, fontWeight: 700, color: PALETTE.ink, margin: 0, lineHeight: 1.25, letterSpacing: '-0.012em' }}>
                      {g.title}
                    </h2>
                    <p style={{ fontSize: 13.5, color: PALETTE.slate, lineHeight: 1.6, marginTop: 10, flexGrow: 1 }}>
                      {g.cardSummary}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 18, paddingTop: 14, borderTop: `1px solid ${PALETTE.border}` }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: PALETTE.muted }}>
                        <Clock size={13} /> {g.readMinutes} {t('min read')}
                      </span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: PALETTE.magenta }}>
                        {t('Read guide')} <ArrowRight size={14} />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}

          {/* Newsletter */}
          <div style={{ maxWidth: 720 }}>
            <NewsletterSignup source="guides-index" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: PALETTE.ink, color: '#fff', padding: '64px 32px' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 32, flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ fontFamily: serif, fontSize: 'clamp(22px, 2.8vw, 32px)', fontWeight: 700, margin: 0, color: '#fff' }}>
              {t('Prefer to see it working instead of reading about it?')}
            </h2>
            <p style={{ fontSize: 15, color: '#CBD5E1', marginTop: 12, maxWidth: 560, lineHeight: 1.6 }}>
              {t('Everything in these guides, the 30-second trace, the mass balance, the customer-code mapping, is live in the Keystone sandbox.')}
            </p>
          </div>
          <Link to="/site/contact" data-testid="guides-index-cta" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '14px 26px', background: PALETTE.magenta, color: '#fff', fontSize: 14, fontWeight: 700, textDecoration: 'none', borderRadius: 4, flexShrink: 0 }}>
            {t('Book a discovery call')} <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}
